import { useEffect, useState } from "react";
import Seo from "@/components/base/Seo";
import StatusBadge from "@/components/base/StatusBadge";
import AdminPageHeader from "../components/AdminPageHeader";
import { useAuth } from "@/context/AuthContext";
import { getAdPlacements } from "@/services/adminApi";
import type { AdPlacement } from "@/types";

export default function AdminAdPlacements() {
  const { token } = useAuth();
  const [rows, setRows] = useState<AdPlacement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [notice, setNotice] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    getAdPlacements(token)
      .then((p) => active && setRows(p))
      .catch(() => active && setError(true))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [token]);

  const toggle = (id: string) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, status: r.status === "active" ? "paused" : "active" } : r)));
    setNotice("Placement availability updated.");
    setTimeout(() => setNotice(""), 2200);
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="h-44 animate-pulse rounded-lg border border-background-200 bg-background-100/60"></div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-6 text-center text-sm text-secondary-900">
        Couldn't load ad placements. Please refresh.
      </div>
    );
  }

  return (
    <>
      <Seo title="Ad Placements" path="/admin/ad-placements" />

      <AdminPageHeader
        icon="ri-layout-grid-line"
        title="Ad placements"
        subtitle="Inventory slots available for advertisers across the site."
        actions={
          notice ? (
            <span className="inline-flex items-center gap-2 rounded-md border border-accent-200 bg-accent-50 px-4 py-2 text-sm text-accent-900">
              <i className="ri-checkbox-circle-line text-accent-600"></i> {notice}
            </span>
          ) : undefined
        }
      />

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        {rows.map((p) => (
          <article key={p.id} className="rounded-lg border border-background-200 bg-background-50 p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-secondary-100 text-secondary-800">
                  <i className="ri-layout-grid-line"></i>
                </span>
                <div>
                  <h2 className="font-heading text-base font-bold text-foreground-950">{p.name}</h2>
                  <p className="mt-0.5 text-xs text-foreground-500">Shown on {p.page}</p>
                </div>
              </div>
              <StatusBadge status={p.status} />
            </div>

            <div className="mt-4 grid grid-cols-3 gap-3 text-sm">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-foreground-500">Slot</p>
                <p className="mt-0.5 truncate font-mono text-xs text-foreground-700">{p.slot}</p>
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-foreground-500">Size</p>
                <p className="mt-0.5 text-foreground-800">{p.size}</p>
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-foreground-500">Impressions</p>
                <p className="mt-0.5 text-foreground-800">{p.impressions.toLocaleString()}</p>
              </div>
            </div>

            <div className="mt-4 flex items-center justify-between border-t border-background-100 pt-4">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-foreground-500">
                <i className={p.status === "active" ? "ri-checkbox-circle-line text-accent-600" : "ri-pause-circle-line"}></i>
                {p.status === "active" ? "Accepting bookings" : "Paused"}
              </span>
              <button
                type="button"
                onClick={() => toggle(p.id)}
                className="inline-flex items-center gap-1 rounded-md border border-background-300 px-3 py-1.5 text-xs font-semibold text-foreground-700 hover:border-primary-400 whitespace-nowrap cursor-pointer"
              >
                <i className={p.status === "active" ? "ri-pause-line" : "ri-play-line"}></i>
                {p.status === "active" ? "Pause slot" : "Activate"}
              </button>
            </div>
          </article>
        ))}
      </div>
    </>
  );
}